import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, ThumbsUp, ThumbsDown, AlertCircle } from "lucide-react";
import { toast } from "react-toastify";
import {
  getArticleFeedback,
  getArticleFeedbackRatios,
  getArticles,
} from "./supportApi";

const formatDate = (date) => {
  if (!date) return "N/A";
  return new Date(date).toLocaleString("en-US");
};

/** ratio is a 0–1 fraction, and null when nothing has been voted — not 0. */
const formatRatio = (ratio) =>
  ratio === null || ratio === undefined ? "—" : `${Math.round(ratio * 100)}%`;

export default function ArticleFeedback() {
  const [activeTab, setActiveTab] = useState("ratios");

  // articleId -> title. ArticleFeedback deliberately has no relation to Article, so the
  // rows carry an id and nothing else; this is the only way to show a name.
  const [titles, setTitles] = useState({});

  const [ratios, setRatios] = useState([]);
  const [isRatiosLoading, setIsRatiosLoading] = useState(true);

  const [votes, setVotes] = useState([]);
  const [votesPagination, setVotesPagination] = useState({
    total: 0,
    page: 1,
    limit: 20,
  });
  const [isVotesLoading, setIsVotesLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [helpfulFilter, setHelpfulFilter] = useState("");
  const [commentFilter, setCommentFilter] = useState("");
  const [articleFilter, setArticleFilter] = useState("");

  const limit = 20;
  const totalPages = Math.max(1, Math.ceil(votesPagination.total / limit));

  /** A vote can outlive its article — that is the point of storing no foreign key. */
  const titleFor = (articleId) =>
    titles[articleId] || `Deleted article (${articleId.slice(0, 8)}…)`;

  useEffect(() => {
    const load = async () => {
      try {
        // One call covers the whole library; there are 44 articles.
        const data = await getArticles(1, 100);
        const map = {};
        for (const a of data.data || []) {
          map[a.id] = a.translations?.en?.title || a.translations?.fa?.title || a.id;
        }
        setTitles(map);
      } catch (error) {
        console.error("Error loading article titles:", error);
      }
    };
    load();
  }, []);

  useEffect(() => {
    const load = async () => {
      setIsRatiosLoading(true);
      try {
        const data = await getArticleFeedbackRatios();
        // Already ordered worst-first by the backend. Re-sorting here would undo the
        // rule that volume beats percentage.
        setRatios(data.ratios || []);
      } catch (error) {
        console.error("Error loading feedback ratios:", error);
        toast.error("Failed to load the ratings summary");
      } finally {
        setIsRatiosLoading(false);
      }
    };
    load();
  }, []);

  const loadVotes = useCallback(async () => {
    setIsVotesLoading(true);
    try {
      const filters = {};
      if (helpfulFilter) filters.helpful = helpfulFilter;
      if (commentFilter) filters.withComment = commentFilter;
      if (articleFilter) filters.articleId = articleFilter;

      const data = await getArticleFeedback(filters, page, limit);
      setVotes(data.feedback || []);
      setVotesPagination({
        total: data.total ?? 0,
        page: data.page ?? page,
        limit: data.limit ?? limit,
      });
    } catch (error) {
      console.error("Error loading feedback:", error);
      toast.error("Failed to load the feedback");
    } finally {
      setIsVotesLoading(false);
    }
  }, [helpfulFilter, commentFilter, articleFilter, page]);

  useEffect(() => {
    if (activeTab === "votes") loadVotes();
  }, [activeTab, loadVotes]);

  const openVotesFor = (articleId) => {
    setArticleFilter(articleId);
    setHelpfulFilter("");
    setCommentFilter("");
    setPage(1);
    setActiveTab("votes");
  };

  const selectClass =
    "rounded-md border border-border/70 bg-white/[0.03] px-2.5 py-1.5 text-xs text-foreground focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30";

  return (
    <div className="">
      {/* Page header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
          <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
          Support
        </div>
        <h1 className="font-serif italic text-[36px] leading-[1.05] text-foreground mb-1">
          Article <span className="text-gradient-ginox">Feedback</span>
        </h1>
        <p className="text-sm text-muted-foreground mb-4">
          What readers thought of each article. Comments are only asked for when someone
          marks an article unhelpful.
        </p>
      </div>

      {/* Tabs */}
      <div className="md:flex md:flex-row flex flex-col gap-3 mb-8">
        {[
          ["ratios", "Worst rated"],
          ["votes", "All feedback"],
        ].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={
              activeTab === key
                ? "rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green"
                : "rounded-md border border-border/70 bg-white/[0.03] px-4 py-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
            }
          >
            {label}
          </button>
        ))}
      </div>

      <div className="space-y-5">
        {/* ---------------------------------------------------------------- ratios */}
        {activeTab === "ratios" &&
          (isRatiosLoading ? (
            <div className="py-10 flex justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-ginox-green" />
            </div>
          ) : ratios.length === 0 ? (
            <div className="flex items-start gap-2 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
              <AlertCircle
                className="h-4 w-4 text-yellow-400 mt-0.5 shrink-0"
                aria-hidden="true"
              />
              <p className="text-yellow-400 text-sm">
                No one has rated an article yet. Ratings appear here as readers use the
                help center.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-separate border-spacing-0 rounded-lg border border-border/70 bg-card overflow-hidden">
                <thead>
                  <tr>
                    <th className="border-b border-border/70 px-4 py-3 text-left text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                      Article
                    </th>
                    <th className="border-b border-border/70 px-4 py-3 text-left text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                      Helpful
                    </th>
                    <th className="border-b border-border/70 px-4 py-3 text-left text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                      Unhelpful
                    </th>
                    <th className="border-b border-border/70 px-4 py-3 text-left text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                      Ratio
                    </th>
                    <th className="border-b border-border/70 px-4 py-3 text-center text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {ratios.map((r) => (
                    <tr
                      key={r.articleId}
                      className="hover:bg-white/[0.04] transition"
                    >
                      <td className="border-b border-border/40 px-4 py-3 text-sm text-foreground max-w-md">
                        <span className="line-clamp-1">{titleFor(r.articleId)}</span>
                      </td>
                      <td className="border-b border-border/40 px-4 py-3 text-sm tabular-nums text-ginox-green">
                        {r.helpful}
                      </td>
                      <td className="border-b border-border/40 px-4 py-3 text-sm tabular-nums text-red-400">
                        {r.unhelpful}
                      </td>
                      <td className="border-b border-border/40 px-4 py-3 text-sm tabular-nums text-muted-foreground">
                        {formatRatio(r.ratio)}
                      </td>
                      <td className="border-b border-border/40 px-4 py-3">
                        <div className="flex justify-center">
                          <button
                            onClick={() => openVotesFor(r.articleId)}
                            className="rounded-md border border-border/70 bg-white/[0.03] px-3 py-1.5 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors"
                          >
                            View feedback
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}

        {/* ----------------------------------------------------------------- votes */}
        {activeTab === "votes" && (
          <>
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex flex-col">
                <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
                  Rating
                </label>
                <select
                  value={helpfulFilter}
                  onChange={(e) => {
                    setHelpfulFilter(e.target.value);
                    setPage(1);
                  }}
                  className={`${selectClass} min-w-[140px]`}
                >
                  <option value="">All ratings</option>
                  <option value="true">Helpful</option>
                  <option value="false">Unhelpful</option>
                </select>
              </div>
              <div className="flex flex-col">
                <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
                  Comment
                </label>
                <select
                  value={commentFilter}
                  onChange={(e) => {
                    setCommentFilter(e.target.value);
                    setPage(1);
                  }}
                  className={`${selectClass} min-w-[150px]`}
                >
                  <option value="">All</option>
                  <option value="true">With a comment</option>
                  <option value="false">Without a comment</option>
                </select>
              </div>
              {articleFilter && (
                <button
                  type="button"
                  onClick={() => {
                    setArticleFilter("");
                    setPage(1);
                  }}
                  className="rounded-md border border-border/70 bg-white/[0.03] px-3 py-1.5 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors"
                >
                  Showing one article — show all
                </button>
              )}
            </div>

            {isVotesLoading ? (
              <div className="py-10 flex justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-ginox-green" />
              </div>
            ) : votes.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                {helpfulFilter || commentFilter || articleFilter
                  ? "No feedback matches these filters."
                  : "No one has rated an article yet."}
              </p>
            ) : (
              <>
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                  {votesPagination.total}{" "}
                  {votesPagination.total === 1 ? "response" : "responses"}
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full border-separate border-spacing-0 rounded-lg border border-border/70 bg-card overflow-hidden">
                    <thead>
                      <tr>
                        <th className="border-b border-border/70 px-4 py-3 text-left text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                          Article
                        </th>
                        <th className="border-b border-border/70 px-4 py-3 text-center text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                          Rating
                        </th>
                        <th className="border-b border-border/70 px-4 py-3 text-left text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                          Language
                        </th>
                        <th className="border-b border-border/70 px-4 py-3 text-left text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                          Comment
                        </th>
                        <th className="border-b border-border/70 px-4 py-3 text-left text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                          When
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {votes.map((v) => (
                        <tr key={v.id} className="hover:bg-white/[0.04] transition">
                          <td className="border-b border-border/40 px-4 py-3 text-sm text-foreground max-w-xs">
                            <span className="line-clamp-1">
                              {titleFor(v.articleId)}
                            </span>
                          </td>
                          <td className="border-b border-border/40 px-4 py-3">
                            <div className="flex justify-center">
                              {v.helpful ? (
                                <ThumbsUp className="h-4 w-4 text-ginox-green" />
                              ) : (
                                <ThumbsDown className="h-4 w-4 text-red-400" />
                              )}
                            </div>
                          </td>
                          <td className="border-b border-border/40 px-4 py-3 text-sm font-mono uppercase text-muted-foreground">
                            {v.language}
                          </td>
                          {/* Free text a stranger typed — rendered as text, never as markup. */}
                          <td className="border-b border-border/40 px-4 py-3 text-sm text-muted-foreground max-w-md">
                            {v.comment ? (
                              <span className="line-clamp-2">{v.comment}</span>
                            ) : (
                              <span className="text-muted-foreground/50">—</span>
                            )}
                          </td>
                          <td className="border-b border-border/40 px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">
                            {formatDate(v.createdAt)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {totalPages > 1 && (
                  <div className="flex items-center justify-between py-3 text-xs font-mono text-muted-foreground border-t border-border/40">
                    <span className="tabular-nums">
                      {(page - 1) * limit + 1} -{" "}
                      {Math.min(page * limit, votesPagination.total)} of{" "}
                      {votesPagination.total}
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className="flex h-6 w-6 items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        aria-label="Previous page"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>
                      <span className="tabular-nums">
                        {page} / {totalPages}
                      </span>
                      <button
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={page >= totalPages}
                        className="flex h-6 w-6 items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        aria-label="Next page"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
